import type { BorderStyle, Padding } from "./types";

export const MAX_COLUMNS = 4;
export const MIN_COLUMN_PERCENT = 1;
export const MIN_COLUMN_PX = 24;

export type ColumnPresetId =
  | 'one'
  | 'two-equal'
  | 'two-wide-left'
  | 'two-wide-right'
  | 'three-equal'
  | 'four-equal';

export type ColumnLayoutPreset = 'equal' | 'wide-left' | 'wide-right' | 'custom';

export type ColumnPreset = {
  id: ColumnPresetId;
  label: string;
  ratios: number[];
};

export const COLUMN_PRESETS: ColumnPreset[] = [
  { id: 'one', label: '1 col', ratios: [1] },
  { id: 'two-equal', label: '2 cols', ratios: [1, 1] },
  { id: 'two-wide-left', label: 'Wide L', ratios: [2, 1] },
  { id: 'two-wide-right', label: 'Wide R', ratios: [1, 2] },
  { id: 'three-equal', label: '3 cols', ratios: [1, 1, 1] },
  { id: 'four-equal', label: '4 cols', ratios: [1, 1, 1, 1] },
];

export function presetById(id: ColumnPresetId): ColumnPreset {
  const preset = COLUMN_PRESETS.find(item => item.id === id);
  if (!preset) {
    throw new Error(`Unknown column preset: ${id}`);
  }
  return preset;
}

function bandBorderHorizontalWidth(border: BorderStyle | undefined): number {
  if (!border || border.style === "none") {
    return 0;
  }
  return border.width.left + border.width.right;
}

export function rowInnerWidth(
  contentWidth: number,
  padding: Padding,
  border?: BorderStyle,
): number {
  return Math.max(
    0,
    contentWidth -
      bandBorderHorizontalWidth(border) -
      padding.left -
      padding.right,
  );
}

export function emailRowAreaWidth(
  contentWidth: number,
  pagePadding: Padding,
): number {
  return rowInnerWidth(contentWidth, pagePadding);
}

export function columnAvailableWidth(
  innerWidth: number,
  gap: number,
  columnCount: number,
): number {
  return Math.max(0, innerWidth - gap * Math.max(0, columnCount - 1));
}

function distributeByRatios(amount: number, ratios: readonly number[]): number[] {
  const total = ratios.reduce((sum, ratio) => sum + ratio, 0);
  if (total <= 0 || ratios.length === 0) {
    return [];
  }
  if (amount <= 0) {
    return ratios.map(() => 0);
  }

  const exact = ratios.map(ratio => (ratio / total) * amount);
  const floors = exact.map(value => Math.floor(value));
  let remainder = amount - floors.reduce((sum, value) => sum + value, 0);
  const order = exact
    .map((value, index) => ({ index, fraction: value - floors[index] }))
    .sort((left, right) => right.fraction - left.fraction);

  const parts = [...floors];
  for (let index = 0; remainder > 0 && index < order.length; index += 1) {
    const target = order[index];
    if (!target) {
      continue;
    }
    parts[target.index] += 1;
    remainder -= 1;
  }

  return parts;
}

export function ratiosToPercents(ratios: readonly number[]): number[] {
  return distributeByRatios(100, ratios);
}

export function evenPercents(count: number): number[] {
  if (count <= 0) {
    return [];
  }
  return ratiosToPercents(Array.from({ length: count }, () => 1));
}

export function layoutPresetRatios(
  preset: Exclude<ColumnLayoutPreset, 'custom'>,
  columnCount: number,
): number[] {
  if (columnCount <= 0) {
    return [];
  }
  if (preset === 'equal') {
    return Array.from({ length: columnCount }, () => 1);
  }
  if (preset === 'wide-left') {
    return [2, ...Array.from({ length: columnCount - 1 }, () => 1)];
  }
  return [...Array.from({ length: columnCount - 1 }, () => 1), 2];
}

export function layoutPresetPercents(
  preset: Exclude<ColumnLayoutPreset, 'custom'>,
  columnCount: number,
): number[] {
  return ratiosToPercents(layoutPresetRatios(preset, columnCount));
}

export function distributePercentsWithMins(
  ratios: readonly number[],
  minPercents: readonly number[],
): number[] {
  const columnCount = ratios.length;
  if (columnCount <= 0) {
    return [];
  }

  const mins = Array.from({ length: columnCount }, (_, index) =>
    Math.max(MIN_COLUMN_PERCENT, minPercents[index] ?? MIN_COLUMN_PERCENT),
  );
  const minSum = mins.reduce((sum, min) => sum + min, 0);
  if (minSum >= 100) {
    return normalizePercents(mins);
  }

  const slack = distributeByRatios(100 - minSum, ratios);
  return mins.map((min, index) => min + (slack[index] ?? 0));
}

export function rebalancePercentsToFitContentMins(
  widths: readonly number[],
  minPercents: readonly number[],
): number[] {
  if (widths.length <= 1) {
    return widths.length === 1 ? [100] : [];
  }

  const mins = widths.map((_, index) =>
    Math.max(MIN_COLUMN_PERCENT, minPercents[index] ?? MIN_COLUMN_PERCENT),
  );

  const deficits = widths.map((width, index) =>
    Math.max(0, mins[index] - width),
  );
  const totalDeficit = deficits.reduce((sum, deficit) => sum + deficit, 0);
  if (totalDeficit === 0) {
    return [...widths];
  }

  const excess = widths.map((width, index) =>
    Math.max(0, width - mins[index]),
  );
  const totalExcess = excess.reduce((sum, value) => sum + value, 0);

  if (totalExcess < totalDeficit) {
    return distributePercentsWithMins(widths, minPercents);
  }

  const next = [...widths];

  for (let index = 0; index < next.length; index += 1) {
    if (deficits[index] > 0) {
      next[index] = mins[index];
    }
  }

  const donorIndices = excess
    .map((value, index) => (value > 0 ? index : -1))
    .filter(index => index >= 0);
  let assignedReduction = 0;

  for (let donorIndex = 0; donorIndex < donorIndices.length; donorIndex += 1) {
    const index = donorIndices[donorIndex];
    if (index < 0) {
      continue;
    }
    const isLast = donorIndex === donorIndices.length - 1;
    const reduction = isLast
      ? totalDeficit - assignedReduction
      : Math.round((excess[index] / totalExcess) * totalDeficit);
    next[index] = widths[index] - reduction;
    assignedReduction += reduction;
  }

  const drift = 100 - next.reduce((sum, width) => sum + width, 0);
  if (drift !== 0 && donorIndices.length > 0) {
    const lastDonor = donorIndices[donorIndices.length - 1];
    if (lastDonor !== undefined) {
      next[lastDonor] = Math.max(mins[lastDonor], next[lastDonor] + drift);
    }
  }

  return next;
}

export function layoutPresetPercentsWithMins(
  preset: Exclude<ColumnLayoutPreset, 'custom'>,
  columnCount: number,
  minPercents: readonly number[],
): number[] {
  if (columnCount <= 0) {
    return [];
  }

  const target = layoutPresetPercents(preset, columnCount);
  return rebalancePercentsToFitContentMins(target, minPercents);
}

export function matchColumnLayoutPreset(
  widths: readonly number[],
  columnCount: number,
): ColumnLayoutPreset {
  if (columnCount <= 1) {
    return 'equal';
  }

  const presets: Array<Exclude<ColumnLayoutPreset, 'custom'>> = [
    'equal',
    'wide-left',
    'wide-right',
  ];
  for (const preset of presets) {
    const expected = layoutPresetPercents(preset, columnCount);
    if (
      expected.length === widths.length &&
      expected.every((value, index) => value === widths[index])
    ) {
      return preset;
    }
  }

  return 'custom';
}

export function normalizePercents(widths: readonly number[]): number[] {
  if (widths.length === 0) {
    return [];
  }

  const total = widths.reduce((sum, width) => sum + width, 0);
  if (total <= 0) {
    return evenPercents(widths.length);
  }

  return ratiosToPercents(widths);
}

export function clampPairColumnWidths(
  widths: readonly number[],
  leftIndex: number,
  leftMinPercent: number,
  rightMinPercent: number,
): number[] {
  const pairSum = widths[leftIndex] + widths[leftIndex + 1];
  const leftMax = pairSum - rightMinPercent;
  const leftMin = Math.min(leftMinPercent, leftMax);
  const nextLeft = Math.max(leftMin, Math.min(leftMax, widths[leftIndex]));
  const next = [...widths];
  next[leftIndex] = nextLeft;
  next[leftIndex + 1] = pairSum - nextLeft;
  return next;
}

export function rebalancePercents(
  widths: readonly number[],
  index: number,
  nextPercent: number,
  minPercents: readonly number[] = widths.map(() => MIN_COLUMN_PERCENT),
): number[] {
  if (widths.length === 0 || index < 0 || index >= widths.length) {
    return [...widths];
  }
  if (widths.length === 1) {
    return [100];
  }

  const minEdited = minPercents[index] ?? MIN_COLUMN_PERCENT;
  const othersMinTotal = minPercents.reduce(
    (sum, min, columnIndex) => (columnIndex === index ? sum : sum + min),
    0,
  );
  const maxEdited = 100 - othersMinTotal;
  const clamped = Math.max(
    minEdited,
    Math.min(maxEdited, Math.round(nextPercent)),
  );

  if (widths.length === 2) {
    const otherIndex = index === 0 ? 1 : 0;
    const otherMin = minPercents[otherIndex] ?? MIN_COLUMN_PERCENT;
    const otherWidth = 100 - clamped;
    if (otherWidth < otherMin) {
      return index === 0
        ? [100 - otherMin, otherMin]
        : [otherMin, 100 - otherMin];
    }
    return index === 0 ? [clamped, otherWidth] : [otherWidth, clamped];
  }

  const next = [...widths];
  next[index] = clamped;
  const remaining = 100 - clamped;
  const others: Array<{ width: number; columnIndex: number }> = [];
  for (let columnIndex = 0; columnIndex < widths.length; columnIndex += 1) {
    if (columnIndex === index) {
      continue;
    }
    others.push({ width: widths[columnIndex], columnIndex });
  }
  const otherTotal = others.reduce((sum, entry) => sum + entry.width, 0) || others.length;

  let assigned = 0;
  for (let entryIndex = 0; entryIndex < others.length; entryIndex += 1) {
    const entry = others[entryIndex];
    if (!entry) {
      continue;
    }
    const isLast = entryIndex === others.length - 1;
    const floor = minPercents[entry.columnIndex] ?? MIN_COLUMN_PERCENT;
    const share = isLast
      ? remaining - assigned
      : Math.max(floor, Math.round((entry.width / otherTotal) * remaining));
    next[entry.columnIndex] = share;
    assigned += share;
  }

  const drift = 100 - next.reduce((sum, width) => sum + width, 0);
  if (drift !== 0) {
    const lastOther = others[others.length - 1]?.columnIndex;
    if (lastOther !== undefined) {
      const floor = minPercents[lastOther] ?? MIN_COLUMN_PERCENT;
      next[lastOther] = Math.max(floor, next[lastOther] + drift);
    }
  }

  return next;
}

export function percentMinFromPx(minPx: number, available: number): number {
  if (available <= 0) {
    return MIN_COLUMN_PERCENT;
  }
  return Math.max(MIN_COLUMN_PERCENT, Math.ceil((minPx / available) * 100));
}

export function resolveColumnPixelWidths(
  percents: readonly number[],
  innerWidth: number,
  gap: number,
): number[] {
  const count = percents.length;
  if (count === 0) {
    return [];
  }

  const available = columnAvailableWidth(innerWidth, gap, count);
  const pixels = percents.map(percent =>
    Math.round((percent / 100) * available),
  );
  const drift = available - pixels.reduce((sum, width) => sum + width, 0);
  if (drift !== 0) {
    pixels[pixels.length - 1] = Math.max(0, pixels[pixels.length - 1] + drift);
  }

  return pixels;
}

export function columnPreviewFlexWeights(percents: readonly number[]): number[] {
  if (percents.length === 0) {
    return [];
  }
  const total = percents.reduce((sum, percent) => sum + percent, 0);
  if (total <= 0) {
    return percents.map(() => 1 / percents.length);
  }
  return percents.map(percent => percent / total);
}
